import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Checkbox } from "@/components/ui/checkbox"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"

const formSchema = z.object({
  workingModels: z.array(z.enum(["onsite", "remote", "freelancer", "hybrid"])),
  gender: z.enum(["any", "male", "female"]),
  workingTime: z.string().optional(),
})

export function WorkingConditionsForm({ formData, updateFormData, onNext, onPrevious }) {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workingModels: formData.workingModels,
      gender: formData.gender,
      workingTime: formData.workingTime || "",
    },
  })

  const onSubmit = (data) => {
    updateFormData(data)
    onNext()
  }

  const workingModelOptions = [
    { id: "onsite", label: "On-site" },
    { id: "remote", label: "Remote" },
    { id: "freelancer", label: "Freelancer" },
    { id: "hybrid", label: "Hybrid" },
  ]

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="workingModels"
          render={() => (
            <FormItem>
              <div className="mb-4">
                <FormLabel>Working Models</FormLabel>
                <FormDescription>Select all applicable working models for this position</FormDescription>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {workingModelOptions.map((option) => (
                  <FormField
                    key={option.id}
                    control={form.control}
                    name="workingModels"
                    render={({ field }) => {
                      return (
                        <FormItem key={option.id} className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(option.id)}
                              onCheckedChange={(checked) => {
                                return checked
                                  ? field.onChange([...field.value, option.id])
                                  : field.onChange(field.value?.filter((value) => value !== option.id))
                              }}
                            />
                          </FormControl>
                          <FormLabel className="font-normal">{option.label}</FormLabel>
                        </FormItem>
                      )
                    }}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="gender"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Gender Preference</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender preference" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>Specify if there's a gender preference for this role</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="workingTime"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Working Time</FormLabel>
              <FormControl>
                <Textarea placeholder="e.g., Monday to Friday, 9 AM to 5 PM" {...field} />
              </FormControl>
              <FormDescription>Describe the expected working hours or schedule</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={onPrevious}>
            Previous
          </Button>
          <Button type="submit">Next</Button>
        </div>
      </form>
    </Form>
  )
}

